import { useCallback, useMemo } from 'react'
import { SLITE_UTM_CONTEXT_KEY, getItemSafe } from '../helpers/localStorage'

type AnalyticsObject = {
  track: (name: string, data?: Object, options?: Object) => void
  group: (orgId: string, traits: Object, options?: Object) => void
  identify: (userId: string, traits: Object, options?: Object) => void
  page: (title: string, traits: Object, options?: Object) => void
  user: () => { anonymousId: () => string }
  ready: (callback: () => void) => void
  getAnonymousId: () => string
  setAnonymousId: (anonymousId: string) => void
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    analytics?: AnalyticsObject
    rudderanalytics?: AnalyticsObject
  }
}

export function getAnalyticsObject() {
  return window.analytics ?? window.rudderanalytics
}

const CONTEXT_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'ga_clientid',
  'fbclid',
  'slt_device',
]

function fromEntries(iterable: Iterable<[string, unknown]>) {
  return [...iterable].reduce((obj, [key, val]) => {
    obj[key] = val
    return obj
  }, {} as Record<string, unknown>)
}

export function getAnalyticsRawContext(): Record<
  string,
  string | null | undefined
> {
  const utmContextStr = getItemSafe(SLITE_UTM_CONTEXT_KEY)
  if (utmContextStr) {
    try {
      const utmContext = JSON.parse(utmContextStr)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return fromEntries(
        Object.entries(utmContext)
          .filter(([key]) => CONTEXT_FIELDS.includes(key))
          .map(([key, value]) => [key, value?.toString()])
      )
    } catch {
      // Noop
    }
  }

  const searchParams = new URLSearchParams(window.location.search)
  return {
    utm_source: searchParams.get('utm_source'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_medium: searchParams.get('utm_medium'),
    utm_content: searchParams.get('utm_content'),
    utm_term: searchParams.get('utm_term'),
  }
}

export function getAnalyticsContext() {
  return {
    campaign: fromEntries(
      Object.entries(getAnalyticsRawContext())
        .filter(([k]) => k.startsWith('utm_'))
        .map(([k, value]) => [k.replace('_utm', ''), value])
    ),
  }
}

export function useAnalytics() {
  const context = useMemo(() => getAnalyticsContext(), [])

  return useCallback(
    (eventName: string, traits: Record<string, unknown> = {}) => {
      const analytics = getAnalyticsObject()
      if (!analytics) {
        return
      }

      analytics.track(
        eventName,
        {
          ...traits,
        },
        { context }
      )
    },
    [context]
  )
}
